import { useMutation } from 'react-query'
import { queryClient } from 'src/query-client'
import { appFeatureQueryKeys } from 'src/query-keys'
import {
  AppFeatureEnabledState,
  AppFeatureToggleRow,
  CustomerAppFeatureMappingRow,
  FeatureCustomerAssignmentRow,
  UpdateAdminAppFeatureBody,
  UpdateCustomerAppFeatureMappingItem,
  UpdateFeatureCustomerAssignmentItem,
} from 'src/generated-types/app-feature'
import { requestProvider } from './requestProvider'
import { useGet, usePut } from './reactQuery'

const baseUrl = () => 'app-features'
const adminBaseUrl = () => 'admin/app-features'
const customerBaseUrl = (customerId: EntityId) => `admin/customers/${customerId}/app-features`

export const appFeatureEndpoints = {
  getAppFeatureMap(customerIds?: EntityId[]) {
    if (!customerIds?.length) return baseUrl()
    return `${baseUrl()}?${customerIds.map(id => `customerIds=${id}`).join('&')}`
  },
  getAdminAppFeatures() {
    return adminBaseUrl()
  },
  patchAdminAppFeature(featureId: EntityId) {
    return `${adminBaseUrl()}/${featureId}`
  },
  getFeatureCustomerAssignments(featureId: EntityId) {
    return `${adminBaseUrl()}/${featureId}/customers`
  },
  updateFeatureCustomerAssignments(featureId: EntityId) {
    return `${adminBaseUrl()}/${featureId}/customers`
  },
  getCustomerAppFeatureMappings(customerId: EntityId) {
    return customerBaseUrl(customerId)
  },
  updateCustomerAppFeatureMappings(customerId: EntityId) {
    return customerBaseUrl(customerId)
  },
}

export const invalidateAppFeatureCache = {
  getAppFeatureMap(customerIds?: EntityId[]) {
    queryClient.invalidateQueries(appFeatureQueryKeys.GET_APP_FEATURE_MAP(customerIds))
  },
  getAdminAppFeatures() {
    queryClient.invalidateQueries(appFeatureQueryKeys.GET_ADMIN_APP_FEATURES())
  },
  getCustomerAppFeatureMappings(customerId: EntityId) {
    queryClient.invalidateQueries(appFeatureQueryKeys.GET_CUSTOMER_APP_FEATURES(customerId))
  },
  getFeatureCustomerAssignments(featureId: EntityId) {
    queryClient.invalidateQueries(appFeatureQueryKeys.GET_FEATURE_CUSTOMERS(featureId))
  },
}

export const useGetAppFeatureMap = (customerIds?: EntityId[], options?: { enabled?: boolean }) =>
  useGet<AppFeatureEnabledState>(
    appFeatureEndpoints.getAppFeatureMap(customerIds),
    undefined,
    {
      queryKey: appFeatureQueryKeys.GET_APP_FEATURE_MAP(customerIds),
      enabled: options?.enabled ?? (customerIds == null || customerIds.length > 0),
    },
  )

export const useGetAdminAppFeatures = () =>
  useGet<AppFeatureToggleRow[]>(appFeatureEndpoints.getAdminAppFeatures(), undefined, {
    queryKey: appFeatureQueryKeys.GET_ADMIN_APP_FEATURES(),
  })

/** Patched per row from the admin grid, so the feature id travels with the payload instead of the hook. */
export const usePatchAdminAppFeature = () =>
  useMutation(({ id, body }: { id: EntityId; body: UpdateAdminAppFeatureBody }) =>
    requestProvider.patch<AppFeatureToggleRow>(appFeatureEndpoints.patchAdminAppFeature(id), body),
  )

export const useGetFeatureCustomerAssignments = (featureId: EntityId) =>
  useGet<FeatureCustomerAssignmentRow[]>(appFeatureEndpoints.getFeatureCustomerAssignments(featureId), undefined, {
    queryKey: appFeatureQueryKeys.GET_FEATURE_CUSTOMERS(featureId),
    enabled: Number(featureId) > 0,
  })

export const useUpdateFeatureCustomerAssignments = (featureId: EntityId) =>
  usePut<FeatureCustomerAssignmentRow[], UpdateFeatureCustomerAssignmentItem[]>(
    appFeatureEndpoints.updateFeatureCustomerAssignments(featureId),
  )

export const useGetCustomerAppFeatureMappings = (customerId: EntityId) =>
  useGet<CustomerAppFeatureMappingRow[]>(appFeatureEndpoints.getCustomerAppFeatureMappings(customerId), undefined, {
    queryKey: appFeatureQueryKeys.GET_CUSTOMER_APP_FEATURES(customerId),
    enabled: Number(customerId) > 0,
  })

export const useUpdateCustomerAppFeatureMappings = (customerId: EntityId) =>
  usePut<CustomerAppFeatureMappingRow[], UpdateCustomerAppFeatureMappingItem[]>(
    appFeatureEndpoints.updateCustomerAppFeatureMappings(customerId),
  )
